/**
 * Decorative flat illustration for the Welcome banner: a laptop showing a
 * dashboard, a floating growth-chart tile, a task-list card and a plant.
 * Drawn in code so it follows the theme (brand purple, light and dark).
 */
export default function WelcomeIllustration({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 380 230"
      className={className}
      role="img"
      aria-label=""
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <linearGradient id="wi-primary" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.95" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0.65" />
        </linearGradient>
        <linearGradient id="wi-leaf" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#34d399" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
        <linearGradient id="wi-leaf-2" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#6ee7b7" />
          <stop offset="100%" stopColor="#10b981" />
        </linearGradient>
      </defs>

      {/* soft ground shadow */}
      <ellipse cx="190" cy="212" rx="150" ry="9" className="fill-foreground/10" />

      {/* laptop base + screen */}
      <path d="M52 176h230l12 18a6 6 0 0 1-5 9H45a6 6 0 0 1-5-9z" className="fill-muted stroke-border" strokeWidth="1.5" />
      <rect x="68" y="34" width="198" height="142" rx="12" className="fill-card stroke-border" strokeWidth="1.5" />
      <rect x="78" y="44" width="178" height="122" rx="7" className="fill-muted/60" />

      {/* dashboard on the screen */}
      <rect x="86" y="52" width="26" height="106" rx="5" className="fill-card" />
      <rect x="91" y="60" width="16" height="5" rx="2.5" fill="url(#wi-primary)" />
      <rect x="91" y="72" width="16" height="4" rx="2" className="fill-muted-foreground/30" />
      <rect x="91" y="82" width="16" height="4" rx="2" className="fill-muted-foreground/30" />
      <rect x="91" y="92" width="16" height="4" rx="2" className="fill-muted-foreground/30" />
      <rect x="120" y="52" width="46" height="34" rx="5" className="fill-card" />
      <rect x="126" y="58" width="34" height="22" rx="3" fill="url(#wi-primary)" opacity="0.85" />
      <rect x="172" y="52" width="38" height="34" rx="5" className="fill-card" />
      <rect x="178" y="58" width="26" height="22" rx="3" fill="#fda4af" />
      <rect x="216" y="52" width="34" height="34" rx="5" className="fill-card" />
      <rect x="222" y="58" width="22" height="22" rx="3" fill="#6ee7b7" />
      <rect x="120" y="94" width="130" height="64" rx="5" className="fill-card" />
      <path
        d="M130 142 L150 126 L168 134 L190 112 L212 120 L240 102"
        fill="none"
        stroke="hsl(var(--primary))"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M130 142 L150 126 L168 134 L190 112 L212 120 L240 102 L240 150 L130 150 Z" fill="hsl(var(--primary))" opacity="0.08" />

      {/* floating growth tile */}
      <g>
        <rect x="14" y="26" width="76" height="58" rx="12" fill="url(#wi-primary)" />
        <rect x="14" y="26" width="76" height="58" rx="12" fill="none" stroke="white" strokeOpacity="0.35" strokeWidth="1.5" />
        <path d="M28 66 L44 52 L56 60 L76 42" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M66 42 H76 V52" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" />
      </g>

      {/* floating task-list card */}
      <g>
        <rect x="22" y="112" width="84" height="62" rx="11" className="fill-card stroke-border" strokeWidth="1.5" />
        <circle cx="38" cy="130" r="4" fill="#34d399" />
        <rect x="48" y="127" width="46" height="5" rx="2.5" className="fill-muted-foreground/40" />
        <circle cx="38" cy="145" r="4" fill="hsl(var(--primary))" opacity="0.8" />
        <rect x="48" y="142" width="36" height="5" rx="2.5" className="fill-muted-foreground/30" />
        <circle cx="38" cy="160" r="4" className="fill-muted-foreground/40" />
        <rect x="48" y="157" width="42" height="5" rx="2.5" className="fill-muted-foreground/30" />
      </g>

      {/* plant */}
      <g>
        <path d="M300 150 C 286 130, 288 104, 306 90 C 318 108, 316 134, 300 150 Z" fill="url(#wi-leaf)" />
        <path d="M312 148 C 326 126, 340 108, 360 104 C 360 126, 340 144, 312 148 Z" fill="url(#wi-leaf-2)" />
        <path d="M304 150 C 296 126, 310 100, 328 80 C 342 100, 332 132, 304 150 Z" fill="url(#wi-leaf)" opacity="0.92" />
        <path d="M300 150 C 305 126, 312 108, 324 92" fill="none" stroke="white" strokeOpacity="0.45" strokeWidth="1.5" strokeLinecap="round" />
        <path d="M296 150 H 338 L 332 200 a8 8 0 0 1 -8 7 H 310 a8 8 0 0 1 -8 -7 Z" className="fill-card stroke-border" strokeWidth="1.5" />
        <rect x="292" y="144" width="50" height="10" rx="5" className="fill-muted stroke-border" strokeWidth="1.5" />
      </g>
    </svg>
  )
}
